import { Branch, getAlpha2Code } from 'assets/models/Branch';
import { parsePhoneNumber, isValidPhoneNumber } from 'libphonenumber-js';
import { SITUATIONS } from 'modules/shared/util/situations';
import { VehicleWeightOption, VehicleWeightOptionLite } from 'assets/models/enums/VehicleWeightOption';
import { Driver } from 'assets/models/Driver';
import { Division } from 'assets/constants/enums';
import { GlobalStateService } from 'modules/core/services/state/global-state';
import * as Sentry from '@sentry/angular';

type EntityType = 'user' | 'provider' | 'driver';

interface CognitoAttribute {
	Name: string;
	Value: string;
}

export const handleError = (err, message) => {
	console.error(message, err);
	Sentry.captureException(err);
};

export function sortByName(item) {
	return item.sort((a: any, b: any) => a.name.localeCompare(b.name));
}

/**
 * It takes a phone number and a branch, and returns a formatted phone number
 * @param {string} phone - The phone number to format.
 * @param {Branch} branch - The branch of the phone number.
 * @returns A function that takes two arguments, phone and branch, and returns a string.
 */
export function formatPhoneNumber(phone: string, branch: Branch) {
	if (!phone) phone = '';
	if (isValidPhoneNumber(phone, getAlpha2Code(branch))) {
		const parsedPhoneNumber = parsePhoneNumber(phone, getAlpha2Code(branch));
		return parsedPhoneNumber.formatNational();
	}
	return phone;
}

export function formatPhoneNumberInternational(phone: string, branch: Branch) {
	if (!phone) phone = '';
	if (isValidPhoneNumber(phone, getAlpha2Code(branch))) {
		const parsedPhoneNumber = parsePhoneNumber(phone, getAlpha2Code(branch));
		return parsedPhoneNumber.number;
	}
	return '';
}

/**
 * Sanitize phone number
 * IE: (787) 1211 1112 -> 78712111112
 */
export function sanitizePhoneNumber(phoneNumber: string): string {
	return phoneNumber.replace(/\D/g, '');
}

/**
 * If the situation is not a tow, return the lite version of the vehicle weight options, otherwise
 * return the full version
 * @param {string} situation - string - the situation that the user has selected
 * @returns the VehicleWeightOptionLite or VehicleWeightOption component.
 */
export function weightOptionItems(situation: string) {
	const towWeightOptions = [
		SITUATIONS.TOW_BREAKDOWN,
		SITUATIONS.TOW_COLLISION,
		SITUATIONS.TOW_COLLISSION,
		SITUATIONS.TOW_EXTRACTION,
		SITUATIONS.TOW_TIRE,
	];
	if (!towWeightOptions.includes(situation)) return VehicleWeightOptionLite;
	else return VehicleWeightOption;
}

/**
 * Check if parameter has a numeric value, that means any number will return true, including 0
 * @param value the value to validate
 * @returns true if the value is a number
 */
export function numValueExist(value: any) {
	return typeof value === 'number';
}

/**
 * Filters drivers based on their division and branch.
 *
 * Checks if a driver's branch matches the current branch, and if the driver has any skills
 * matching the given division. If both conditions are met, the driver is considered for inclusion in the
 * filtered list.
 */
export function filterDriversByDivision(
	driver: Partial<Driver>,
	division: Division,
	globalState: GlobalStateService,
): boolean {
	// Se obtienen los drivers sólo del branch actual
	if (driver.branch === globalState.getBranch()) {
		// Si el driver tiene algún skill que sea de tipo home, se agrega al filtro dropdown de drivers
		return driver.skills?.some((skill) => {
			const serviceType = globalState.getServiceTypeByName(skill);
			return serviceType?.type === division;
		});
	}
}

/**
 * The function `getEnumKeyByValue` retrieves the key of an enum object based on a given value.
 * @param {any} enumObj - The `enumObj` parameter in the `getEnumKeyByValue` function is an object
 * representing an enum. It contains key-value pairs where the keys are the enum keys and the values
 * are the corresponding enum values.
 * @param {string} value - The `value` parameter in the `getEnumKeyByValue` function is the value that
 * you want to find the corresponding key for in the provided `enumObj`. The function will search
 * through the enum object to find a key that matches the given value and return that key if found.
 * @returns The function `getEnumKeyByValue` returns the key of an enum object that corresponds to a
 * given value, or `undefined` if no matching key is found.
 */
export function getEnumKeyByValue(enumObj: any, value: string): string | undefined {
	const entries = Object.entries(enumObj);
	for (const [key, enumValue] of entries) {
		if (enumValue === value) {
			return key;
		}
	}
	return undefined;
}

/**
 * It takes a data object and an entity type, and returns an array of Cognito attributes
 * @param {any} data - The data object to be transformed.
 * @param {EntityType} entityType - The type of entity to be transformed.
 * @returns An array of Cognito attributes.
 */
export function transformAppDataToCognitoAttributes(data: any, entityType: EntityType): CognitoAttribute[] {
	const commonAttributes = {
		email: data.email,
	};

	const attributeTransformers: Record<EntityType, (data: any) => Record<string, any>> = {
		user: (data) => ({
			'custom:audara_id': data.audaraId || '',
			'custom:role': data.roles ? data.roles.join(',') : '',
			'custom:default_division': data.defaultDivision,
			'custom:default_account': data.defaultAccount,
			'custom:mongo_id': data._id,
			'custom:branch': data.branch,
			'custom:status': data.status,
			given_name: data.firstname,
			family_name: data.lastname,
		}),
		provider: (data) => {
			const specificAttributes: Record<string, any> = {
				'custom:iconset': data.iconset || 'default',
				'custom:name': data.name,
				'custom:phone': data.phone,
				'custom:phone2': data.phone2,
				'custom:providerId': data.providerId,
				'custom:providerName': data.providerName,
				'custom:roles': data.roles ? data.roles.join(',') : '',
				'custom:vendorId': data.vendorId,
				'custom:mongo_id': data._id,
				'custom:branch': data.branch,
				'custom:status': data.status,
			};

			if (data.phone || data.phone2) {
				const phoneToUse = data.phone || data.phone2;
				specificAttributes['phone_number'] = formatPhoneNumberInternational(phoneToUse, data.branch);
				specificAttributes['phone_number_verified'] = true;
			}

			return specificAttributes;
		},
		driver: (data) => {
			const attributes: Record<string, any> = {
				name: data.name,
				email: data.email,
				address: data.address,
				'custom:country': data.branch,
				'custom:mongo_provider_id': data.providerId,
			};
			const phoneNumber = formatPhoneNumberInternational(data.phone, data.branch);
			if (phoneNumber) {
				attributes['phone_number'] = phoneNumber;
				attributes['phone_number_verified'] = true;
			}
			return attributes;
		},
	};

	const specificAttributes = attributeTransformers[entityType](data);
	const attributes = { ...commonAttributes, ...specificAttributes };

	return Object.entries(attributes)
		.filter(([_, value]) => value != null && value !== '')
		.map(([key, value]) => ({
			Name: key,
			Value: value.toString(),
		}));
}
